
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgentFormData } from "@/models/agent-form";
import { AgentService } from "@/services/agent-service";
import { toast } from "@/components/ui/use-toast";
import { CreateAgentData } from "@/models/agent";
import FormButton from "@/components/agent-upload/FormButton";
import { useAuth } from "@/contexts/auth-context";
import { SubmitHandler } from "react-hook-form";

interface AgentFormSubmitHandlerProps {
  onSubmit: (handler: SubmitHandler<AgentFormData>) => void;
  formState: {
    isSubmitting: boolean;
  };
}

const AgentFormSubmitHandler: React.FC<AgentFormSubmitHandlerProps> = ({ 
  onSubmit, 
  formState 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit: SubmitHandler<AgentFormData> = async (data) => {
    try {
      setLoading(true);
      setError(null);
      console.log("Starting agent creation process with data:", data);
      
      // Validate user is logged in
      if (!user || !user.id) {
        throw new Error("You must be logged in to create an agent");
      }
      
      // Upload the file first
      let fileUrl = "";
      if (data.file) {
        console.log("Uploading agent file...");
        try {
          const uploadedFileUrl = await AgentService.uploadAgentFile(data.file);
          if (!uploadedFileUrl) {
            throw new Error("Failed to upload file");
          }
          fileUrl = uploadedFileUrl;
          console.log("File uploaded successfully:", fileUrl);
        } catch (fileError: any) {
          console.error("File upload error:", fileError);
          throw new Error("Failed to upload file: " + (fileError.message || "Unknown error"));
        }
      }
      
      // Convert tags, features, and use cases to arrays
      const tagsArray = data.tags ? data.tags.split(",").map(tag => tag.trim()).filter(tag => tag !== "") : [];
      const featuresArray = data.features ? data.features.split("\n").map(feature => feature.trim()).filter(feature => feature !== "") : [];
      const useCasesArray = data.useCases ? data.useCases.split("\n").map(useCase => useCase.trim()).filter(useCase => useCase !== "") : [];
      
      // Create agent in database
      const agentData: CreateAgentData = {
        name: data.name,
        description: data.description,
        category: data.category,
        tags: tagsArray,
        pricingModel: data.pricingModel,
        fullDescription: data.fullDescription,
        features: featuresArray,
        useCases: useCasesArray,
        requirements: data.requirements,
        version: data.version,
        license: "MIT", // Default license since we're removing the field
        fileUrl,
        isPublic: data.isPublic,
        ownerId: user.id,
        customCode: data.customCode || "",
        promptTemplate: data.promptTemplate || ""
      };
      
      console.log("Creating agent with data:", agentData);
      
      try {
        const newAgent = await AgentService.createAgent(agentData);
        
        if (!newAgent) {
          throw new Error("Failed to create agent in database");
        }
        
        console.log("Agent created successfully:", newAgent);
        
        // Set success state
        setSuccess(true);
        
        toast({
          title: "Success!",
          description: "Your agent has been created successfully.",
        });
        
        // Navigate to the agent page after a short delay
        setTimeout(() => {
          navigate(`/agent/${newAgent.id}`);
        }, 1500);
      } catch (dbError: any) {
        console.error("Database error:", dbError);
        throw new Error("Failed to save agent to database: " + (dbError.message || "Unknown error"));
      }
    } catch (error: any) {
      console.error("Error creating agent:", error);
      
      setError(error.message || "Failed to create your agent");
      
      toast({
        title: "Error",
        description: error.message || "Failed to create your agent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-2 rounded-md mb-6">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}
      <FormButton
        loading={loading}
        success={success}
        disabled={formState.isSubmitting}
        type="submit"
        onClick={() => onSubmit(handleSubmit)}
      />
    </>
  );
};

export default AgentFormSubmitHandler;
